import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Calendar,
  RefreshCw,
  Clock,
  CheckCircle,
  History,
  Loader2,
  AlertCircle,
  Trophy,
  Users,
  Play,
  Search,
  Zap,
} from 'lucide-react';
import { Button } from '../common/Button';
import { subscribeToPath, callFunction } from '../../config/firebase';
import { safeDistanceToNow as formatDistanceToNow, safeFormat as format } from '../../utils/safeFormat';
import { useTestMode } from '../../contexts/TestModeContext';

// League configuration matching functions/index.js
const LEAGUES = [
  { id: 'nfl', name: 'NFL', sport: 'Football', icon: '🏈' },
  { id: 'ncaaf', name: 'NCAAF', sport: 'Football', icon: '🏈' },
  { id: 'nba', name: 'NBA', sport: 'Basketball', icon: '🏀' },
  { id: 'ncaab', name: 'NCAAB', sport: 'Basketball', icon: '🏀' },
  { id: 'mlb', name: 'MLB', sport: 'Baseball', icon: '⚾' },
  { id: 'nhl', name: 'NHL', sport: 'Hockey', icon: '🏒' },
  { id: 'mls', name: 'MLS', sport: 'Soccer', icon: '⚽' },
  { id: 'epl', name: 'EPL', sport: 'Soccer', icon: '⚽' },
  { id: 'laliga', name: 'La Liga', sport: 'Soccer', icon: '⚽' },
  { id: 'bundesliga', name: 'Bundesliga', sport: 'Soccer', icon: '⚽' },
  { id: 'seriea', name: 'Serie A', sport: 'Soccer', icon: '⚽' },
  { id: 'ligue1', name: 'Ligue 1', sport: 'Soccer', icon: '⚽' },
  { id: 'ufc', name: 'UFC', sport: 'MMA', icon: '🥊' },
];

interface GameData {
  gameId: string;
  leagueId: string;
  sportKey?: string;
  sportTitle?: string;
  homeTeam: string;
  awayTeam: string;
  commenceTime: number;
  fetchedAt: number;
  status?: 'scheduled' | 'live' | 'completed';
  homeScore?: number;
  awayScore?: number;
  odds?: Array<{ name: string; price: number }>;
  pickCount?: number;
  finalScore?: { home: number; away: number };
  completedAt?: number;
  associatedPicks?: string[];
}

// Game Card Component
const GameCard: React.FC<{
  game: GameData;
  isHistory?: boolean;
  onMoveToHistory?: () => void;
}> = ({ game, isHistory = false, onMoveToHistory }) => {
  const isLive = game.status === 'live' ||
    (game.commenceTime <= Date.now() && game.commenceTime > Date.now() - 4 * 60 * 60 * 1000);
  const isPast = game.commenceTime < Date.now() - 4 * 60 * 60 * 1000;

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="p-4 hover:bg-surface-light dark:hover:bg-gray-800/50 transition-colors border-b border-border-light dark:border-border-dark last:border-b-0"
    >
      <div className="flex items-center justify-between">
        <div className="flex-1 min-w-0">
          {/* Teams */}
          <div className="flex items-center gap-2">
            <span className="font-medium text-sm truncate">{game.awayTeam}</span>
            <span className="text-text-secondary-light dark:text-text-secondary-dark text-xs">@</span>
            <span className="font-medium text-sm truncate">{game.homeTeam}</span>
          </div>

          {/* Game Info */}
          <div className="flex items-center gap-3 mt-1 text-xs text-text-secondary-light dark:text-text-secondary-dark">
            <span className="flex items-center gap-1">
              <Calendar size={12} />
              {format(game.commenceTime, 'MMM d, h:mm a')}
            </span>
            {game.pickCount !== undefined && game.pickCount > 0 && (
              <span className="flex items-center gap-1 text-primary">
                <Users size={12} />
                {game.pickCount} picks
              </span>
            )}
          </div>

          {/* Score for completed games */}
          {isHistory && game.finalScore && (
            <div className="mt-2 text-sm font-bold">
              {game.awayTeam}: {game.finalScore.away} - {game.homeTeam}: {game.finalScore.home}
            </div>
          )}
        </div>

        {/* Status Badge */}
        <div className="flex items-center gap-2">
          {isLive ? (
            <span className="px-2 py-1 rounded-full text-xs font-medium bg-success/10 text-success flex items-center gap-1">
              <Play size={10} className="fill-current" />
              LIVE
            </span>
          ) : isPast && !isHistory ? (
            <span className="px-2 py-1 rounded-full text-xs font-medium bg-warning/10 text-warning">
              Ended
            </span>
          ) : isHistory ? (
            <span className="px-2 py-1 rounded-full text-xs font-medium bg-gray-500/10 text-gray-500">
              Completed
            </span>
          ) : (
            <span className="px-2 py-1 rounded-full text-xs font-medium bg-info/10 text-info flex items-center gap-1">
              <Clock size={10} />
              {formatDistanceToNow(game.commenceTime, { addSuffix: true })}
            </span>
          )}
        </div>
      </div>
    </motion.div>
  );
};

// Main Schedule Tab Component
export const ScheduleTab: React.FC = () => {
  const [activeLeague, setActiveLeague] = useState('nfl');
  const [showHistory, setShowHistory] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isRunningAutoClose, setIsRunningAutoClose] = useState(false);
  const [autoCloseResult, setAutoCloseResult] = useState<{ success: boolean; message: string } | null>(null);
  const [refreshResult, setRefreshResult] = useState<{
    success: boolean;
    message: string;
    details?: string;
    debug?: string[];
    apiCredits?: { used: string; remaining: string };
  } | null>(null);
  const [games, setGames] = useState<GameData[]>([]);
  const [historyGames, setHistoryGames] = useState<GameData[]>([]);
  const [loading, setLoading] = useState(true);
  const [lastUpdate, setLastUpdate] = useState<number | null>(null);
  const [lastAutoFetch, setLastAutoFetch] = useState<{ runAt: number; success: boolean; summary: string | null } | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const { getPath, isTestMode } = useTestMode();

  // Subscribe to last monthly auto-fetch run
  useEffect(() => {
    const unsub = subscribeToPath(getPath('systemLogs/latestRun/monthlyGamesFetch'), (data: any) => {
      if (data && data.runAt) {
        setLastAutoFetch({ runAt: data.runAt, success: data.success, summary: data.summary });
      }
    });
    return () => unsub();
  }, [getPath]);

  // Load games for selected league
  useEffect(() => {
    setLoading(true);

    const gamesPath = getPath(`gamesData/byLeague/${activeLeague}`);
    const historyPath = getPath(`gamesData/history/byLeague/${activeLeague}`);
    const lastUpdatePath = getPath('gamesData/lastUpdate');

    const unsubGames = subscribeToPath(gamesPath, (data) => {
      if (data) {
        const gamesArray = Object.values(data) as GameData[];
        setGames(gamesArray.sort((a, b) => a.commenceTime - b.commenceTime));
      } else {
        setGames([]);
      }
      setLoading(false);
    });

    const unsubHistory = subscribeToPath(historyPath, (data) => {
      if (data) {
        const historyArray = Object.values(data) as GameData[];
        setHistoryGames(historyArray.sort((a, b) =>
          (b.completedAt || b.commenceTime) - (a.completedAt || a.commenceTime)
        ));
      } else {
        setHistoryGames([]);
      }
    });

    const unsubLastUpdate = subscribeToPath(lastUpdatePath, (data) => {
      setLastUpdate(data as number);
    });

    return () => {
      unsubGames();
      unsubHistory();
      unsubLastUpdate();
    };
  }, [activeLeague, getPath]);

  // Refresh games for selected league
  const handleRefreshGames = async () => {
    setIsRefreshing(true);
    setRefreshResult(null);
    try {
      const result = await callFunction('triggerGamesFetch')({ leagueId: activeLeague });
      console.log('Games refreshed:', result);
      const data = (result as any).data || result;
      const leagueData = data.leagueResults?.[activeLeague] || {};

      setRefreshResult({
        success: true,
        message: `${activeLeagueInfo?.name}: ${leagueData.added || 0} games added, ${leagueData.removed || 0} old games removed`,
        details: leagueData.error
          ? `Warning: ${leagueData.error}`
          : leagueData.gamesFound === 0
            ? 'API returned 0 games (may be off-season or no odds available)'
            : `API status: ${leagueData.apiStatus}, Found: ${leagueData.gamesFound} games`,
        debug: data.debug,
        apiCredits: data.apiCredits
      });
    } catch (error: any) {
      console.error('Error refreshing games:', error);
      const debugInfo = error.details?.debug || [];
      setRefreshResult({
        success: false,
        message: error.message || 'Failed to refresh games',
        debug: debugInfo,
        details: error.code ? `Error code: ${error.code}` : undefined
      });
    } finally {
      setIsRefreshing(false);
    }
  };


  // Manually trigger auto-close function
  const handleRunAutoClose = async () => {
    setIsRunningAutoClose(true);
    setAutoCloseResult(null);
    try {
      const result = await callFunction('manualAutoClose')({
        leagueId: activeLeague,
        isTestMode
      });
      console.log('Auto-close completed:', result);
      setAutoCloseResult({
        success: true,
        message: `Auto-close completed. ${(result as any)?.data?.closedCount || 0} items processed.`,
      });
      // Clear the result after 5 seconds
      setTimeout(() => setAutoCloseResult(null), 5000);
    } catch (error: any) {
      console.error('Error running auto-close:', error);
      setAutoCloseResult({
        success: false,
        message: error.message || 'Failed to run auto-close',
      });
      setTimeout(() => setAutoCloseResult(null), 5000);
    } finally {
      setIsRunningAutoClose(false);
    }
  };

  // Separate games into categories based on current time
  const { upcomingGames, liveGames, endedGames } = useMemo(() => {
    const now = Date.now();
    const fourHoursAgo = now - 4 * 60 * 60 * 1000;

    const upcoming: GameData[] = [];
    const live: GameData[] = [];
    const ended: GameData[] = [];

    games.forEach(game => {
      if (game.commenceTime > now) {
        upcoming.push(game);
      } else if (game.commenceTime > fourHoursAgo) {
        live.push(game);
      } else {
        ended.push(game);
      }
    });

    return {
      upcomingGames: upcoming.sort((a, b) => a.commenceTime - b.commenceTime),
      liveGames: live.sort((a, b) => a.commenceTime - b.commenceTime),
      endedGames: ended.sort((a, b) => b.commenceTime - a.commenceTime), // Most recent first
    };
  }, [games]);

  // Filter by search query
  const filteredUpcoming = useMemo(() => {
    if (!searchQuery) return upcomingGames;
    const query = searchQuery.toLowerCase();
    return upcomingGames.filter(
      game => game.homeTeam.toLowerCase().includes(query) || game.awayTeam.toLowerCase().includes(query)
    );
  }, [upcomingGames, searchQuery]);

  const filteredLive = useMemo(() => {
    if (!searchQuery) return liveGames;
    const query = searchQuery.toLowerCase();
    return liveGames.filter(
      game => game.homeTeam.toLowerCase().includes(query) || game.awayTeam.toLowerCase().includes(query)
    );
  }, [liveGames, searchQuery]);

  const filteredEnded = useMemo(() => {
    if (!searchQuery) return endedGames;
    const query = searchQuery.toLowerCase();
    return endedGames.filter(
      game => game.homeTeam.toLowerCase().includes(query) || game.awayTeam.toLowerCase().includes(query)
    );
  }, [endedGames, searchQuery]);

  const filteredHistoryGames = useMemo(() => {
    if (!searchQuery) return historyGames;
    const query = searchQuery.toLowerCase();
    return historyGames.filter(
      game =>
        game.homeTeam.toLowerCase().includes(query) ||
        game.awayTeam.toLowerCase().includes(query)
    );
  }, [historyGames, searchQuery]);

  const activeLeagueInfo = LEAGUES.find(l => l.id === activeLeague);

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6"
    >
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold flex items-center gap-2">
            <Calendar size={24} className="text-primary" />
            Game Schedule
          </h2>
          <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark mt-1">
            Manage game data across all leagues
          </p>
        </div>
        <div className="text-right space-y-1">
          {lastUpdate && (
            <div className="text-xs text-text-secondary-light dark:text-text-secondary-dark">
              Last sync: {formatDistanceToNow(lastUpdate, { addSuffix: true })}
            </div>
          )}
          {lastAutoFetch && (
            <div className={`text-xs flex items-center justify-end gap-1 ${lastAutoFetch.success ? 'text-green-600 dark:text-green-400' : 'text-red-500'}`}>
              {lastAutoFetch.success ? <CheckCircle size={10} /> : <AlertCircle size={10} />}
              Auto-fetch: {formatDistanceToNow(lastAutoFetch.runAt, { addSuffix: true })}
            </div>
          )}
          {isTestMode && (
            <span className="inline-block mt-1 px-2 py-0.5 text-xs font-medium bg-warning/10 text-warning rounded">
              Test Mode
            </span>
          )}
        </div>
      </div>

      {/* League Sub-tabs */}
      <div className="overflow-x-auto -mx-4 px-4 pb-2">
        <div className="flex gap-2 min-w-max p-2 bg-surface-light dark:bg-surface-dark rounded-xl">
          {LEAGUES.map((league) => (
            <button
              key={league.id}
              onClick={() => setActiveLeague(league.id)}
              className={`px-3 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap flex items-center gap-1.5 ${
                activeLeague === league.id
                  ? 'bg-primary text-black shadow-sm'
                  : 'text-text-secondary-light dark:text-text-secondary-dark hover:bg-white/50 dark:hover:bg-gray-800/50'
              }`}
            >
              <span>{league.icon}</span>
              {league.name}
            </button>
          ))}
        </div>
      </div>

      {/* Actions */}
      <div className="flex flex-wrap gap-3">
        <Button
          variant="primary"
          icon={isRefreshing ? <Loader2 size={18} className="animate-spin" /> : <RefreshCw size={18} />}
          onClick={handleRefreshGames}
          disabled={isRefreshing}
        >
          {isRefreshing ? 'Refreshing...' : `Refresh ${activeLeagueInfo?.name}`}
        </Button>
        <Button
          variant="outline"
          icon={isRunningAutoClose ? <Loader2 size={18} className="animate-spin" /> : <Zap size={18} />}
          onClick={handleRunAutoClose}
          disabled={isRunningAutoClose || isRefreshing}
          className="border-warning text-warning hover:bg-warning/10"
        >
          {isRunningAutoClose ? 'Running...' : 'Run Auto-Close'}
        </Button>
        <Button
          variant={showHistory ? 'primary' : 'outline'}
          icon={<History size={18} />}
          onClick={() => setShowHistory(!showHistory)}
        >
          {showHistory ? 'Hide History' : 'Show History'}
        </Button>
      </div>

      {/* API Info Banner */}
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3 text-xs text-blue-700 dark:text-blue-300">
        <strong>Automated + On-Demand:</strong> Games are auto-fetched every Monday 3 AM UTC for configured leagues.
        You can also manually refresh individual leagues here. Data is cached for 6 hours.
      </div>

      {/* Refresh Result */}
      <AnimatePresence>
        {refreshResult && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className={`p-4 rounded-xl ${
              refreshResult.success
                ? 'bg-info/10 border border-info/20'
                : 'bg-error/10 border border-error/20'
            }`}
          >
            <div className="flex items-start gap-3">
              {refreshResult.success ? (
                <CheckCircle size={20} className="text-info flex-shrink-0 mt-0.5" />
              ) : (
                <AlertCircle size={20} className="text-error flex-shrink-0 mt-0.5" />
              )}
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2">
                  <span className={`font-medium ${refreshResult.success ? 'text-info' : 'text-error'}`}>
                    {refreshResult.message}
                  </span>
                  {refreshResult.apiCredits && (
                    <span className="text-xs bg-gray-500/20 px-2 py-0.5 rounded text-text-secondary-light dark:text-text-secondary-dark">
                      API: {refreshResult.apiCredits.remaining} credits left
                    </span>
                  )}
                </div>
                {refreshResult.details && (
                  <p className="text-xs text-text-secondary-light dark:text-text-secondary-dark mt-1">
                    {refreshResult.details}
                  </p>
                )}
                {refreshResult.debug && refreshResult.debug.length > 0 && (
                  <details className="mt-2">
                    <summary className="text-xs cursor-pointer text-text-secondary-light dark:text-text-secondary-dark hover:text-text-light dark:hover:text-text-dark">
                      Show debug logs ({refreshResult.debug.length} entries)
                    </summary>
                    <div className="mt-2 max-h-64 overflow-y-auto bg-black/20 rounded p-2">
                      <pre className="text-xs text-text-secondary-light dark:text-text-secondary-dark whitespace-pre-wrap font-mono">
                        {refreshResult.debug.join('\n')}
                      </pre>
                    </div>
                  </details>
                )}
              </div>
              <button
                onClick={() => setRefreshResult(null)}
                className="text-text-secondary-light dark:text-text-secondary-dark hover:text-text-light dark:hover:text-text-dark"
              >
                ×
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Auto-Close Result */}
      <AnimatePresence>
        {autoCloseResult && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className={`p-4 rounded-xl flex items-center gap-3 ${
              autoCloseResult.success
                ? 'bg-success/10 border border-success/20'
                : 'bg-error/10 border border-error/20'
            }`}
          >
            {autoCloseResult.success ? (
              <CheckCircle size={20} className="text-success" />
            ) : (
              <AlertCircle size={20} className="text-error" />
            )}
            <span className={autoCloseResult.success ? 'text-success' : 'text-error'}>
              {autoCloseResult.message}
            </span>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <div className="card p-4">
          <div className="flex items-center gap-2 mb-1">
            <Clock size={16} className="text-info" />
            <span className="text-xs text-text-secondary-light dark:text-text-secondary-dark">
              Upcoming
            </span>
          </div>
          <p className="text-2xl font-bold">{upcomingGames.length}</p>
        </div>
        <div className="card p-4">
          <div className="flex items-center gap-2 mb-1">
            <Play size={16} className="text-success fill-success" />
            <span className="text-xs text-text-secondary-light dark:text-text-secondary-dark">
              Live Now
            </span>
          </div>
          <p className="text-2xl font-bold text-success">{liveGames.length}</p>
        </div>
        <div className="card p-4">
          <div className="flex items-center gap-2 mb-1">
            <AlertCircle size={16} className="text-warning" />
            <span className="text-xs text-text-secondary-light dark:text-text-secondary-dark">
              Ended (Not Moved)
            </span>
          </div>
          <p className="text-2xl font-bold text-warning">{endedGames.length}</p>
        </div>
        <div className="card p-4">
          <div className="flex items-center gap-2 mb-1">
            <Trophy size={16} className="text-gray-500" />
            <span className="text-xs text-text-secondary-light dark:text-text-secondary-dark">
              History
            </span>
          </div>
          <p className="text-2xl font-bold">{historyGames.length}</p>
        </div>
      </div>

      {/* Search */}
      <div className="relative">
        <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-secondary-light dark:text-text-secondary-dark" />
        <input
          type="text"
          placeholder="Search teams..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full pl-10 pr-4 py-2.5 bg-surface-light dark:bg-surface-dark border border-border-light dark:border-border-dark rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
        />
      </div>

      {/* Games List */}
      <div className="space-y-4">
        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 size={32} className="animate-spin text-primary" />
          </div>
        ) : (
          <>
            {/* Live Games */}
            {filteredLive.length > 0 && (
              <div className="card overflow-hidden border-2 border-success/30">
                <div className="p-4 border-b border-border-light dark:border-border-dark bg-success/5">
                  <h3 className="font-bold flex items-center gap-2 text-success">
                    <Play size={18} className="fill-success" />
                    Live Games
                    <span className="text-xs font-normal opacity-70">
                      ({filteredLive.length})
                    </span>
                  </h3>
                </div>
                <div>
                  {filteredLive.map((game) => (
                    <GameCard key={game.gameId} game={game} />
                  ))}
                </div>
              </div>
            )}

            {/* Upcoming Games */}
            <div className="card overflow-hidden">
              <div className="p-4 border-b border-border-light dark:border-border-dark bg-surface-light/50 dark:bg-gray-800/30">
                <h3 className="font-bold flex items-center gap-2">
                  <Clock size={18} className="text-info" />
                  Upcoming Games
                  <span className="text-xs font-normal text-text-secondary-light dark:text-text-secondary-dark">
                    ({filteredUpcoming.length})
                  </span>
                </h3>
              </div>
              {filteredUpcoming.length === 0 ? (
                <div className="p-8 text-center text-text-secondary-light dark:text-text-secondary-dark">
                  <Calendar size={48} className="mx-auto mb-3 opacity-30" />
                  <p>No upcoming games found for {activeLeagueInfo?.name}</p>
                  <p className="text-xs mt-1">Try refreshing the games data</p>
                </div>
              ) : (
                <div>
                  {filteredUpcoming.map((game) => (
                    <GameCard key={game.gameId} game={game} />
                  ))}
                </div>
              )}
            </div>

            {/* Ended Games (not yet moved to history) */}
            {filteredEnded.length > 0 && (
              <div className="card overflow-hidden border border-warning/30">
                <div className="p-4 border-b border-border-light dark:border-border-dark bg-warning/5">
                  <h3 className="font-bold flex items-center gap-2 text-warning">
                    <AlertCircle size={18} />
                    Ended Games (Need Cleanup)
                    <span className="text-xs font-normal opacity-70">
                      ({filteredEnded.length})
                    </span>
                  </h3>
                  <p className="text-xs text-text-secondary-light dark:text-text-secondary-dark mt-1">
                    These games have ended but haven't been moved to history yet
                  </p>
                </div>
                <div>
                  {filteredEnded.map((game) => (
                    <GameCard key={game.gameId} game={game} />
                  ))}
                </div>
              </div>
            )}

            {/* History Section */}
            <AnimatePresence>
              {showHistory && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className="card overflow-hidden"
                >
                  <div className="p-4 border-b border-border-light dark:border-border-dark bg-surface-light/50 dark:bg-gray-800/30">
                    <h3 className="font-bold flex items-center gap-2">
                      <History size={18} className="text-gray-500" />
                      Game History
                      <span className="text-xs font-normal text-text-secondary-light dark:text-text-secondary-dark">
                        ({filteredHistoryGames.length})
                      </span>
                    </h3>
                  </div>
                  {filteredHistoryGames.length === 0 ? (
                    <div className="p-8 text-center text-text-secondary-light dark:text-text-secondary-dark">
                      <History size={48} className="mx-auto mb-3 opacity-30" />
                      <p>No completed games in history</p>
                    </div>
                  ) : (
                    <div>
                      {filteredHistoryGames.map((game) => (
                        <GameCard key={game.gameId} game={game} isHistory />
                      ))}
                    </div>
                  )}
                </motion.div>
              )}
            </AnimatePresence>
          </>
        )}
      </div>
    </motion.div>
  );
};

export default ScheduleTab;
